import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import styles from "./list.module.css";

import { MeetingOutputDTO, SORT_ORDER, TimeEventOutputDTO, TimeEventQueryMeetingPopulate, TimeEventQueryPopulate, TimeEventSortMethod, TimeEventTargetModels } from "@kalyzee/kast-web-sdk/http";
import { SearchInput, useTriggerable } from "@kalyzee/kast-web-sdk/web-components";
import DatePicker from "react-datepicker";
import { PageMasterMode, usePageContextRef } from "../../../../components/navigation/PageContext";
import { useSettings } from "../../../../hooks/settings";

import { isToday } from "date-fns";
import { useNavigate } from "react-router-dom";
import { MasterUI } from "../../../../components/common/MasterUI";
import { ErrorView } from "../../../../components/utils/ErrorView";
import { TimeEventsAgenda } from "../../../../components/utils/TimeEventsAgenda";
import { SessionMode } from "../../../../contexts/appStore/hooks";
import { useAppStoreSelector } from "../../../../contexts/appStore/selectors";
import { useSdkContext } from "../../../../contexts/sdk/context";
import { VodPlayerContentPage } from "./player";
import clsx from "clsx";
import { PrimaryButton } from "@kalyzee/kalyzee-ui";
import { useDelayedState } from "../../../../hooks/states";
import { set } from "lodash";

export const VodListPage = () => {
  const pageContextRef = usePageContextRef<SessionMode.MASTER>();
  const [date, setDate] = useState<Date>(new Date()); // new Date()
  const isReadyRef = useRef(false);
  const navigate = useNavigate();

  const user = useAppStoreSelector(state => state.user);

  const [selectedEventId, setSelectedEventId] = useState<string>();
  const logoContainerRef = useRef<HTMLImageElement>(null);
  const [settings] = useSettings();

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [search, setSearch] = useState<string>();

  const [result, setResult] = useDelayedState<{ fetching: boolean, events: TimeEventOutputDTO[] | undefined }>({ fetching: false, events: undefined });

  const [currentMeetingId, setCurrentMeetingId] = useState<string>();
  const [error, setError] = useState<any>();

  const sdkCtx = useSdkContext();

  useTriggerable(logoContainerRef, { clickCount: 3 }, () => pageContextRef.current?.openSettingsPopup()); // press 'k' and click 3 times with mouse(left button) on logo

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  const onJoinEvent = (eventId: string) => {
    const selectedEvent = result.events?.find((event) => event.id === eventId);
    const selectedMeeting = selectedEvent ? (selectedEvent.target as MeetingOutputDTO) : undefined;
    if (selectedMeeting) {
      //pageContextRef.current?.goTo(PageMasterMode.REPLAY_PLAYER, { id: selectedMeeting.id });
      setCurrentMeetingId(selectedMeeting.id);
    }
  };

  const onBack = useCallback(() => {
    if (currentMeetingId) {
      setCurrentMeetingId(undefined);
    } else {
      pageContextRef.current?.goTo(PageMasterMode.LIST);
    }
  }, [currentMeetingId]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECT ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    if (!isToday(date)) return;
    // // check date every minute
    const interval = setInterval(() => {
      if (!isToday(date.getDate())) {
        setDate(new Date());
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [date]);

  const loadEvents = useCallback(() => {
    const afterDate = dateRange[0];
    let beforeDate = dateRange[1] ?? afterDate;
    if (beforeDate) {
      beforeDate = new Date(beforeDate.getFullYear(), beforeDate.getMonth(), beforeDate.getDate(), 23, 59, 59); // end of the day
    }
    setResult(prev => ({ ...prev, fetching: true }));
    sdkCtx.client
      .getHttpClient()
      .timeEvent.getAll({
        rooms: undefined,
        targets: [TimeEventTargetModels.Meeting],
        meetingFilter: {
          containsActors: user ? [user.id] : undefined,
          record: true,
          search: search ? { value: search, fields: ["name", "moderator"] } : undefined,
        },
        populate: settings.dev ? [TimeEventQueryPopulate.ROOM] : [],
        meetingPopulate: [TimeEventQueryMeetingPopulate.ACTOR_IDENTITY],
        afterDate: afterDate ?? undefined,
        beforeDate: beforeDate ?? undefined,
        sortMethod: TimeEventSortMethod.START_DATETIME,
        sortOrder: SORT_ORDER.DESCENDING,
      })
      .then((r) => {
        setError(undefined);
        setResult({ fetching: false, events: r.data }, 500);
        if (r.data?.length) setSelectedEventId(r.data[0].id)
      })
      .catch((err) => {
        setError(err);
        setResult(prev => ({ ...prev, fetching: false }), 500);
      })
  }, [user, settings.dev, search, dateRange, sdkCtx.client]);

  useEffect(() => {
    loadEvents();
    isReadyRef.current = true;
  }, []);

  useEffect(() => {
    if (!isReadyRef.current) return;
    if (currentMeetingId) navigate("#" + currentMeetingId);
    else navigate("");
  }, [currentMeetingId]);


  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  if (error) return <ErrorView retry={() => { loadEvents(); }} />;

  const renderSearchOptions = () => {
    return (
      <div style={{ 
        display: "flex",
        flexDirection: "column",
        width: "100%",
        boxShadow: "var(--shadow-element-box)",
        padding: 20,
        gap: "1em",
        borderRadius: 20,
        backgroundColor: "var(--color-element-box-bg)",
      }}>
        <div className={styles.searchContainer}>
          <div className={styles.searchItemContainer} style={{ flex: 1 }}>
            Recherche par nom du cours ou de l'expert
            <SearchInput
              setValue={setSearch}
              className={styles.search}
              nativeProps={{
                onKeyDown: (ev) => {
                  if (ev.key === "Enter") {
                    loadEvents();
                  }
                },
                placeholder: "Jean Dupont, Mathématiques ...",
              }}
            />
          </div>
          <div className={styles.searchItemContainer}>
            Recherche par date
            <DatePicker
              className={clsx({
                [styles.datePicker]: true,
                [styles.ready]: dateRange[0],
              })}
              selectsRange={true}
              startDate={dateRange[0] ?? undefined}
              endDate={dateRange[1] ?? undefined}
              onChange={setDateRange}
              isClearable={true}
              placeholderText="Afficher les calendrier"
            />
          </div>
        </div>
        <PrimaryButton
          text={result.fetching ? "Recherche en cours ..." : "Rechercher"}
          className={clsx({
            [styles.searchBtn]: true,
          })}
          onClick={loadEvents}
        />
      </div>
    );
  }

  const renderEvents = () => {
    return (
      <div className={styles.eventsList}>
        {renderSearchOptions()}
        <TimeEventsAgenda
          timeEvents={result.events ? result.events.filter((event) => (event.target ? true : false)) : undefined}
          placeholdersNb={3}
          selectedEventId={selectedEventId}
          onSelectEvent={setSelectedEventId}
          onJoinEvent={onJoinEvent}
          kind="student"
          mode="replay"
        />
      </div>
    );
  };

  return (
    <MasterUI onBack={onBack}>
      {currentMeetingId && (
        <VodPlayerContentPage
          style={{ position: "absolute", top: 0, left: 0, zIndex: 1, width: "100%", height: "100%", background: "black" }}
          id={currentMeetingId}
        />
      )}
      <div className={styles.backgroundHome}>
        <div className={styles.mainHomeLogged}>
          <div className={styles.mainHomeLoggedContent}>
            {renderEvents()}
          </div>
        </div>
      </div>
    </MasterUI>
  );
};
